/**
 * Org structure — chart layout.
 *
 * One placement pass produces the three chart shapes; the shape only decides
 * which axis depth runs along and whether the last level collapses into a list:
 *
 *   hierarchy  depth ↓, siblings →   — the familiar top-down chart
 *   lateral    depth →, siblings ↓   — long unit names fit without wrapping
 *   compact    top-down, but a parent whose children are all leaves stacks them
 *              beneath it as rows, which is what printed charts do for many peers
 *
 * Coordinates are laid out in an unbounded plane; the view translates and
 * scales that plane rather than re-laying it out, so panning and zooming never
 * touch this file.
 */
import type { OrgModel } from './org-model';

export type ChartView = 'chart' | 'ribbon' | 'compact';

export interface PlacedNode {
  id: string;
  depth: number;
  slot: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** A stacked row under a compact parent rather than a full card. */
  leaf?: boolean;
  /** Position within that stack. */
  si?: number;
}

export interface ChartLayout {
  nodes: Record<string, PlacedNode>;
  links: Array<{ from: PlacedNode; to: PlacedNode; leaf: boolean }>;
  mode: 'h' | 'v';
}

/** Card box, gutters, and the smaller box a compact stack row gets. */
export const W = 272;
export const H = 158;
const GX = 30;
const GY = 80;
const RGX = 94;
const RGY = 22;
const LW = 272;
const LH = 46;
const LGY = 8;

/**
 * @param only  When given, the ONLY units that get laid out. Used to draw one
 *              unit's line of command and nothing else: the rest are not dimmed
 *              or folded, they are absent, so the chart shrinks to the line and
 *              Fit can frame it at a readable zoom.
 */
export const layoutChart = (
  model: OrgModel,
  view: ChartView,
  collapsed: Set<string>,
  only?: ReadonlySet<string> | null
): ChartLayout => {
  const nodes: Record<string, PlacedNode> = {};
  const links: ChartLayout['links'] = [];
  const stacked: string[] = [];
  const horiz = view === 'ribbon';
  let slot = 0;

  const inScope = (id: string) => !only || only.has(id);
  const visibleKids = (id: string) =>
    (collapsed.has(id) ? [] : model.byId[id].kids).filter(inScope);
  /** Compact only stacks when there are peers to save room on and none of them branch. */
  const isStack = (id: string) => {
    const k = visibleKids(id);
    return k.length >= 2 && k.every((c) => visibleKids(c).length === 0);
  };

  const place = (id: string, depth: number) => {
    const kids = visibleKids(id);
    if (view === 'compact' && isStack(id)) {
      nodes[id] = { id, depth, slot: slot++, x: 0, y: 0, w: 0, h: 0 };
      kids.forEach((c, i) => {
        nodes[c] = { id: c, depth: depth + 1, slot: nodes[id].slot, x: 0, y: 0, w: 0, h: 0, leaf: true, si: i };
        stacked.push(c);
      });
      return;
    }
    if (!kids.length) {
      nodes[id] = { id, depth, slot: slot++, x: 0, y: 0, w: 0, h: 0 };
      return;
    }
    kids.forEach((c) => place(c, depth + 1));
    const s = kids.map((c) => nodes[c].slot);
    // A parent sits over the midpoint of its children's span, not their mean,
    // so an uneven subtree does not drag the parent off to one side.
    nodes[id] = { id, depth, slot: (Math.min(...s) + Math.max(...s)) / 2, x: 0, y: 0, w: 0, h: 0 };
  };

  // Several roots can come back (a project with more than one top-level unit,
  // or a filtered subtree). They share the slot counter, so they sit side by side.
  model.roots.filter(inScope).forEach((r) => place(r, 0));

  Object.values(nodes).forEach((n) => {
    if (n.leaf) return;
    n.w = W;
    n.h = H;
    if (horiz) {
      n.x = n.depth * (W + RGX);
      n.y = n.slot * (H + RGY);
    } else {
      n.x = n.slot * (W + GX);
      n.y = n.depth * (H + GY);
    }
  });
  stacked.forEach((c) => {
    const n = nodes[c];
    const p = nodes[model.byId[c].parentId as string];
    n.w = LW;
    n.h = LH;
    n.x = p.x + 22;
    n.y = p.y + p.h + 34 + (n.si || 0) * (LH + LGY);
  });

  Object.values(nodes).forEach((n) => {
    const parentId = model.byId[n.id].parentId;
    if (parentId && nodes[parentId]) {
      links.push({ from: nodes[parentId], to: n, leaf: !!n.leaf });
    }
  });

  return { nodes, links, mode: horiz ? 'h' : 'v' };
};

/** The elbow connecting a parent to a child, rounded at the turns. */
export const wirePath = (p: PlacedNode, c: PlacedNode, mode: 'h' | 'v', leaf: boolean) => {
  if (leaf) {
    const sx = p.x + 22;
    const ly = c.y + c.h / 2;
    const r = Math.min(8, (ly - (p.y + p.h)) / 2);
    return `M${sx},${p.y + p.h} V${ly - r} Q${sx},${ly} ${sx + r},${ly} H${c.x}`;
  }
  if (mode === 'h') {
    const x0 = p.x + p.w;
    const x1 = c.x;
    const mx = x0 + (x1 - x0) / 2;
    const y0 = p.y + p.h / 2;
    const y1 = c.y + c.h / 2;
    if (Math.abs(y1 - y0) < 1) return `M${x0},${y0} H${x1}`;
    const s = y1 > y0 ? 1 : -1;
    const r = Math.min(12, Math.abs(y1 - y0) / 2, (x1 - x0) / 4);
    return `M${x0},${y0} H${mx - r} Q${mx},${y0} ${mx},${y0 + s * r} V${y1 - s * r} Q${mx},${y1} ${mx + r},${y1} H${x1}`;
  }
  const x0 = p.x + p.w / 2;
  const x1 = c.x + c.w / 2;
  const y0 = p.y + p.h;
  const y1 = c.y;
  const my = y0 + (y1 - y0) / 2;
  if (Math.abs(x1 - x0) < 1) return `M${x0},${y0} V${y1}`;
  const s = x1 > x0 ? 1 : -1;
  const r = Math.min(12, Math.abs(x1 - x0) / 2, (y1 - y0) / 4);
  return `M${x0},${y0} V${my - r} Q${x0},${my} ${x0 + s * r},${my} H${x1 - s * r} Q${x1},${my} ${x1},${my + r} V${y1}`;
};

/** The plane the chart occupies, with a margin, for Fit. */
export const layoutBounds = (l: ChartLayout) => {
  const v = Object.values(l.nodes);
  if (!v.length) return { x0: 0, y0: 0, x1: 1, y1: 1 };
  return {
    x0: Math.min(...v.map((n) => n.x)) - 40,
    y0: Math.min(...v.map((n) => n.y)) - 40,
    x1: Math.max(...v.map((n) => n.x + n.w)) + 40,
    y1: Math.max(...v.map((n) => n.y + n.h)) + 40,
  };
};
